// ============================================================================
// Delivery SLA — the WORDING. The rule itself now lives on the server.
//
// The verdict (on_time / breached / excused / unknown), the cutoff it was judged
// against and the escalation that ruled on it all arrive on the row as `breach`.
// This module turns that into the sentence the tooltip shows, and nothing else.
//
// It moved because the Summary counts breaches across the WHOLE filtered range,
// which the browser can only do while it holds every row — and holding every row
// is exactly what stopped this page being paginated. The rules are unchanged;
// they are in oms/logistics/services/breach.py, stated once.
// ============================================================================
import dayjs, { Dayjs } from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { LogisticsOrderRow } from "../types";

dayjs.extend(utc);
dayjs.extend(timezone);

const IST = "Asia/Kolkata";

export type BreachState = "on_time" | "breached" | "excused" | "unknown";

export interface BreachVerdict {
  state: BreachState;
  reason: string; // the full sentence for the tooltip
  dueAt: string | null;
}

// The server names the rule; the page words it. A copy change stays a frontend deploy.
const RULE_LABEL: Record<string, string> = {
  pickup: "Pickup — due 8 PM on the dispatch date",
  standard: "Standard — due 6 PM on the target delivery date",
  express_same_day: "Express same-day — due 5h from the order",
  express_advance: "Express — due 2 PM on the target delivery date",
};

// Why a shipment has no verdict, said plainly rather than as a blank.
const UNKNOWN_REASON: Record<string, string> = {
  cancelled: "This booking was cancelled, so no delivery promise stands against it.",
  pickup: "No dispatch date on this pickup, so there is no 8 PM cutoff to judge it against.",
  no_target_date:
    "No target delivery date is committed on this shipment, so there is no promise to have missed.",
  express:
    "No order-placed time on this express shipment, so we cannot tell a same-day order (5-hour clock) from one placed in advance (2 PM cutoff).",
};

const ist = (v: string) => dayjs.utc(v).tz(IST);
const clock = (d: Dayjs) => (d.minute() ? d.format("h:mm A") : d.format("h A"));
const stamp = (d: Dayjs) => `${d.format("h:mm A")} on ${d.format("D MMM")}`;

// "2h 15m" — the size of a miss, which is what makes a breach actionable rather than binary.
const gap = (from: Dayjs, to: Dayjs): string => {
  const mins = Math.max(0, Math.round(to.diff(from, "minute")));
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
};

const excuseNote = (excuse: NonNullable<LogisticsOrderRow["breach"]>["excuse"]): string => {
  if (!excuse) return "";
  const named = excuse.issue_type || "Escalation";
  const at = excuse.raised_at ? stamp(ist(excuse.raised_at)) : "an unrecorded time";
  const cutoff = excuse.notice_cutoff ? clock(ist(excuse.notice_cutoff)) : null;
  switch (excuse.ground) {
    case "vendor_fault":
      return ` Escalated “${named}” at ${at} — a fault the transporter owns.`;
    case "notice_in_time":
      return ` Not counted as a breach: “${named}” was raised ${at}, before the ${cutoff} notice cutoff.`;
    case "notice_late":
      return ` Escalated “${named}” at ${at} — at or after the ${cutoff} notice cutoff, so it is an excuse after the fact rather than notice.`;
    default:
      return ` Not counted as a breach: “${named}” was raised ${at} — the cause is not the transporter's.`;
  }
};

export const breachOf = (r: LogisticsOrderRow): BreachVerdict => {
  const v = r.breach;
  if (!v) return { state: "unknown", reason: "No SLA verdict on this shipment.", dueAt: null };

  if (v.state === "unknown") {
    return {
      state: "unknown",
      dueAt: null,
      reason:
        UNKNOWN_REASON[v.rule] ??
        "Marked delivered, but no delivery time was recorded — there is nothing to compare against the cutoff.",
    };
  }

  const rule = RULE_LABEL[v.rule] ?? v.rule;
  const cutoff = v.due_at ? ist(v.due_at) : null;
  const landed = v.landed_at ? ist(v.landed_at) : null;
  const dueBy = cutoff ? stamp(cutoff) : "its cutoff";
  let reason: string;

  if (landed && cutoff) {
    reason =
      v.state === "breached"
        ? `${rule}. Due by ${dueBy}; delivered ${stamp(landed)} — ${gap(cutoff, landed)} late.`
        : `${rule}. Due by ${dueBy}; delivered ${stamp(landed)}, with ${gap(landed, cutoff)} to spare.`;
  } else if (cutoff) {
    const now = dayjs().tz(IST);
    reason =
      v.state === "breached"
        ? `${rule}. Due by ${dueBy}; still not delivered — ${gap(cutoff, now)} past the cutoff.`
        : `${rule}. Due by ${dueBy}; not delivered yet, ${gap(now, cutoff)} left.`;
  } else {
    reason = rule;
  }

  return { state: v.state, dueAt: v.due_at, reason: reason + excuseNote(v.excuse) };
};

export const isBreached = (r: LogisticsOrderRow): boolean => breachOf(r).state === "breached";
